/**
 * Logging Service
 *
 * Centralized service for storing, retrieving, and querying message logs
 * across the entire pipeline
 */

import type {
  HookLogEntry,
  SubscriberLogEntry,
  MessageActivityLog,
  LogFilter,
  LogStatistics,
} from './LogEntry';
import { getLogSettings } from './LogSettings';

export class LoggingService {
  private static instance: LoggingService;
  private logs: Map<string, MessageActivityLog> = new Map();

  private constructor() {
    // Storage type for future use
    // getLogSettings().getStorageType();
  }

  /**
   * Get or create singleton instance
   */
  static getInstance(): LoggingService {
    if (!LoggingService.instance) {
      LoggingService.instance = new LoggingService();
    }
    return LoggingService.instance;
  }

  /**
   * Create a new message activity log
   */
  createMessageLog(messageId: string, serverId: string, topic: string, messageType: string): MessageActivityLog {
    const log: MessageActivityLog = {
      messageId,
      serverId,
      topic,
      messageType,
      startTime: new Date().toISOString(),
      status: 'processing',
      protocol: 'unknown',
      direction: 'incoming',
      hookLogs: [],
      subscriberLogs: [],
      summary: {
        totalHooks: 0,
        successHooks: 0,
        failedHooks: 0,
        totalSubscribers: 0,
        deliveredSubscribers: 0,
        failedSubscribers: 0,
      },
    };

    this.logs.set(messageId, log);
    return log;
  }

  /**
   * Log hook execution
   */
  logHookExecution(messageId: string, hookLog: HookLogEntry): void {
    if (!getLogSettings().isHookLoggingEnabled()) {
      return;
    }

    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    log.hookLogs.push(hookLog);

    // Update summary
    log.summary.totalHooks++;
    if (hookLog.status === 'success') {
      log.summary.successHooks++;
    } else if (hookLog.status === 'error') {
      log.summary.failedHooks++;
    }
  }

  /**
   * Log subscriber delivery
   */
  logSubscriberDelivery(messageId: string, subscriberLog: SubscriberLogEntry): void {
    if (!getLogSettings().isSubscriberLoggingEnabled()) {
      return;
    }

    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    log.subscriberLogs.push(subscriberLog);

    // Update summary
    log.summary.totalSubscribers++;
    if (subscriberLog.status === 'delivered') {
      log.summary.deliveredSubscribers++;
    } else if (subscriberLog.status === 'error') {
      log.summary.failedSubscribers++;
    }
  }

  /**
   * Log pipeline start envelope snapshot
   */
  logPipelineStart(messageId: string, envelopeSnapshot: any): void {
    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    log.pipelineStart = {
      timestamp: new Date().toISOString(),
      envelopeSnapshot,
    };
  }

  /**
   * Log pipeline end envelope snapshot
   */
  logPipelineEnd(messageId: string, envelopeSnapshot: any, status: string): void {
    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    log.pipelineEnd = {
      timestamp: new Date().toISOString(),
      envelopeSnapshot,
      status,
    };
  }

  /**
   * Log raw message body
   */
  logRawMessage(messageId: string, rawMessage: any): void {
    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    (log as any).rawMessage = rawMessage;
  }

  /**
   * Complete message processing log
   */
  completeMessageLog(
    messageId: string,
    status: 'completed' | 'failed' | 'aborted',
    metadata?: Record<string, unknown>,
  ): void {
    const log = this.logs.get(messageId);
    if (!log) {
      console.warn(`[LoggingService] Message log not found for ${messageId}`);
      return;
    }

    log.status = status;
    log.endTime = new Date().toISOString();
    log.totalTime = new Date(log.endTime).getTime() - new Date(log.startTime).getTime();
    if (metadata) {
      log.metadata = metadata;
    }

    // Trim old logs if necessary
    this.enforceMaxLogSize();
  }

  /**
   * Get message activity log
   */
  getMessageLog(messageId: string): MessageActivityLog | undefined {
    return this.logs.get(messageId);
  }

  /**
   * Query logs by filter
   */
  queryLogs(filter: LogFilter): MessageActivityLog[] {
    const results: MessageActivityLog[] = [];

    for (const log of this.logs.values()) {
      // Apply filters
      if (filter.messageId && log.messageId !== filter.messageId) continue;
      if (filter.serverId && log.serverId !== filter.serverId) continue;
      if (filter.topic && !log.topic.includes(filter.topic)) continue;
      if (filter.messageType && log.messageType !== filter.messageType) continue;
      if (filter.status && log.status !== filter.status) continue;

      if (filter.startTime) {
        const filterTime = new Date(filter.startTime).getTime();
        const logTime = new Date(log.startTime).getTime();
        if (logTime < filterTime) continue;
      }

      if (filter.endTime) {
        const filterTime = new Date(filter.endTime).getTime();
        const logTime = new Date(log.endTime || log.startTime).getTime();
        if (logTime > filterTime) continue;
      }

      if (filter.hookName) {
        if (!log.hookLogs.some((h) => h.hookName.includes(filter.hookName!))) continue;
      }

      if (filter.subscriberId) {
        if (!log.subscriberLogs.some((s) => s.subscriberId === filter.subscriberId)) continue;
      }

      // Remove snapshots if not requested
      if (!filter.includeSnapshots) {
        delete log.snapshots;
      }

      results.push(log);
    }

    return results;
  }

  /**
   * Get all logs for a server
   */
  getServerLogs(serverId: string): MessageActivityLog[] {
    return Array.from(this.logs.values()).filter((log) => log.serverId === serverId);
  }

  /**
   * Get recent logs
   */
  getRecentLogs(limit: number = 50): MessageActivityLog[] {
    return Array.from(this.logs.values())
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
      .slice(0, limit);
  }

  /**
   * Get log statistics
   */
  getStatistics(): LogStatistics {
    const logs = Array.from(this.logs.values());

    if (logs.length === 0) {
      return {
        totalMessages: 0,
        successMessages: 0,
        failedMessages: 0,
        averageProcessingTime: 0,
        slowestMessage: { messageId: '', time: 0 },
        slowestHook: { hookName: '', time: 0 },
        topicsProcessed: [],
        errorsCount: 0,
        lastMessageTime: new Date().toISOString(),
      };
    }

    const completedLogs = logs.filter((l) => l.status === 'completed');
    const failedLogs = logs.filter((l) => l.status === 'failed');
    const processingTimes = completedLogs.map((l) => l.totalTime || 0);
    const averageTime = processingTimes.length > 0 ? processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length : 0;

    // Find slowest message
    let slowestMessage = { messageId: '', time: 0 };
    for (const log of logs) {
      if ((log.totalTime || 0) > slowestMessage.time) {
        slowestMessage = { messageId: log.messageId, time: log.totalTime || 0 };
      }
    }

    // Find slowest hook
    let slowestHook = { hookName: '', time: 0 };
    for (const log of logs) {
      for (const hookLog of log.hookLogs) {
        if ((hookLog.executionTime || 0) > slowestHook.time) {
          slowestHook = { hookName: hookLog.hookName, time: hookLog.executionTime || 0 };
        }
      }
    }

    // Get unique topics
    const topicsSet = new Set(logs.map((l) => l.topic));

    // Count errors
    let errorsCount = 0;
    for (const log of logs) {
      errorsCount += log.hookLogs.filter((h) => h.status === 'error').length;
      errorsCount += log.subscriberLogs.filter((s) => s.status === 'error').length;
    }

    return {
      totalMessages: logs.length,
      successMessages: completedLogs.length,
      failedMessages: failedLogs.length,
      averageProcessingTime: averageTime,
      slowestMessage,
      slowestHook,
      topicsProcessed: Array.from(topicsSet),
      errorsCount,
      lastMessageTime: logs[logs.length - 1].startTime,
    };
  }

  /**
   * Clear logs for a message
   */
  clearMessageLog(messageId: string): void {
    this.logs.delete(messageId);
  }

  /**
   * Clear all logs
   */
  clearAllLogs(): void {
    this.logs.clear();
  }

  /**
   * Export logs as JSON
   */
  exportLogsAsJSON(): string {
    const data = {
      exportedAt: new Date().toISOString(),
      totalLogs: this.logs.size,
      logs: Array.from(this.logs.values()),
    };
    return JSON.stringify(data, null, 2);
  }

  /**
   * Enforce maximum log size
   */
  private enforceMaxLogSize(): void {
    const maxLogs = getLogSettings().getMaxLogEntries();
    if (this.logs.size > maxLogs) {
      // Remove oldest logs
      const sortedLogs = Array.from(this.logs.entries())
        .sort((a, b) => new Date(a[1].startTime).getTime() - new Date(b[1].startTime).getTime());

      const toRemove = sortedLogs.length - maxLogs;
      for (let i = 0; i < toRemove; i++) {
        this.logs.delete(sortedLogs[i][0]);
      }
    }
  }

  /**
   * Get total number of logs
   */
  getTotalLogCount(): number {
    return this.logs.size;
  }
}

/**
 * Convenience function to access logging service
 */
export function getLoggingService(): LoggingService {
  return LoggingService.getInstance();
}
